/// <amd-module name="TFS/Admin/Views/ProjectPermissions/Components/ProjectPermissionsTable" />
import * as React from "react";
import { IVssPageContext } from "VSS/Platform/Context";

import { Table, ITableColumn, TableColumnStyle, SimpleTableCell, TwoLineTableCell, TableRow, ITableRowDetails } from "VSSUI/Table";
import { GraphMember } from "VSS/Platform/Contracts/Graph";
import { Tooltip } from "VSSUI/TooltipEx";
import { Persona, PersonaSize } from "VSSUI/Persona";
import { PersonaDetailsProvider } from "TFS/Admin/Common/Providers/PersonaDetailsProvider";
import { IIdentity } from "VSSUI/IdentityPicker";
import * as Resources from "TFS/Admin/Views/ProjectPermissions/Resources";
import { IObservableValue, ObservableValue, IObservableArray } from "VSS/Core/Observable";
import { ListSelection } from "VSSUI/List";
import { Filter, FILTER_CHANGE_EVENT } from "VSSUI/Utilities/Filter";
import { Observer } from "VSSUI/Observer";
import { getAssetLocation } from "VSS/Platform/Location";
import { ZeroData } from "VSSUI/ZeroData";
import { Card } from "VSSUI/Card";
import { Dropdown } from "VSSUI/Dropdown";
import * as PermissionLevel from "TFS/Admin/Common/Contracts/PermissionLevel";
import { AdminFeatureTelemetryLogger } from "TFS/Admin/Common/Util/Telemetry/AdminTelemetry";
import { Spinner, SpinnerSize } from "VSSUI/Spinner";
import { DropdownSelection } from "VSSUI/Utilities/DropdownSelection";
import { IListBoxItem } from "VSSUI/ListBox";
import { IProjectPermissionMembers } from "../Contracts/DataModels";
import { ParseErrorFromAPI } from "TFS/Admin/Common/Util/CustomErrorHelper";

export interface ITableComponentProps {
    itemProvider: IObservableArray<IProjectPermissionMembers>;
    pageContext: IVssPageContext;
    filter: Filter;
    filterItemKey: string;
    permissionLevelDefinition: PermissionLevel.PermissionLevelDefinition[];
    permissionLevelDisplayNames: IObservableArray<IListBoxItem<string>>;
    startActionCapture: (action: string, properties: { [key: string]: any }) => AdminFeatureTelemetryLogger;
    fetchMoreItems: () => void;
    updatePermissionLevel: (
        subjecDescriptor: string,
        newDefinitionId: string,
        previousDefinitionId: string
    ) => Promise<PermissionLevel.PermissionLevelAssignment>;
    continuationToken?: string;
}

export class ProjectPermissionsTable extends React.Component<ITableComponentProps> {
    private _selection = new ListSelection(true);
    private _personaDetailsProvider: PersonaDetailsProvider;
    private _itemProvider: IObservableArray<any>;
    private _items: IProjectPermissionMembers[];
    private _lastContinuationToken?: string;

    constructor(props: ITableComponentProps, context: IVssPageContext) {
        super(props);

        this._itemProvider = this.props.itemProvider;
        if (this.props.continuationToken) {
            this._itemProvider.push({
                isLoadItem: true,
                renderRow: this._renderLoadRow
            });
        }
        this._items = this.props.itemProvider.value;
        this._lastContinuationToken = this.props.continuationToken;

        this._personaDetailsProvider = new PersonaDetailsProvider(this.props.pageContext);
    }

    public render(): JSX.Element {
        return (
            <>
                <Observer itemProvider={this._itemProvider}>
                    {(props: { itemProvider: IProjectPermissionMembers[] }) => {
                        return props.itemProvider.length > 0 ? (
                            <Card
                                className="permissions-content flex-grow bolt-card-no-vertical-padding"
                                titleProps={{ text: Resources.ProjectPermissionsSubHeader }}
                            >
                                <Table
                                    columns={this.getColumns()}
                                    itemProvider={this._itemProvider}
                                    selection={this._selection}
                                    onSelect={(event, data) => console.log("Select Row - " + data.index)} // TODO - needs updating
                                />
                            </Card>
                        ) : (
                            <Card className="permissions-content flex-grow bolt-card-no-vertical-padding">
                                <ZeroData
                                    imagePath={getAssetLocation(
                                        this.props.pageContext,
                                        "ms.vss-tfs-web/platform-content/Illustrations/general-no-results-found.svg"
                                    )}
                                    imageAltText="No results for the filter"
                                    primaryText="No results for the filter"
                                />
                            </Card>
                        );
                    }}
                </Observer>
            </>
        );
    }

    public componentDidUpdate() {
        if (this.props.continuationToken && this.props.continuationToken !== this._lastContinuationToken) {
            this._itemProvider.push({
                isLoadItem: true,
                renderRow: this._renderLoadRow
            });
            this._lastContinuationToken = this.props.continuationToken;
        }
    }

    public componentDidMount(): void {
        this.props.filter.subscribe(this._onFilterChanged, FILTER_CHANGE_EVENT);
    }

    public componentWillUnmount(): void {
        this.props.filter.unsubscribe(this._onFilterChanged, FILTER_CHANGE_EVENT);
    }

    private _onFilterChanged = () => {
        const filteredItems = this.filterItems(this._items);
        //TODO: Is there a way to intelligently filter the item provider? (Investigating)
        this._itemProvider.removeAll();
        this._itemProvider.push(...filteredItems);
    };

    private filterItems = (items: IProjectPermissionMembers[]) => {
        if (this.props.filter.hasChangesToReset()) {
            const filterText = this.props.filter.getFilterItemValue<string>(this.props.filterItemKey);
            const filteredItems = items.filter(item => {
                let includeItem = true;
                if (filterText) {
                    includeItem = item.member.displayName
                        ? item.member.displayName.toLocaleLowerCase().indexOf(filterText.toLocaleLowerCase()) !== -1
                        : false;
                }
                return includeItem;
            });
            return filteredItems;
        } else {
            return [...items];
        }
    };

    public getColumns(): ITableColumn<IProjectPermissionMembers>[] {
        return [
            {
                id: "name",
                name: Resources.NameColumnName,
                renderCell: this._renderNameColumn,
                width: new ObservableValue(-33),
                columnStyle: TableColumnStyle.Primary,
                onSize: this._onSizeColumn
            },
            {
                id: "type",
                name: Resources.TypeColumnName,
                renderCell: this._renderTypeColumn,
                width: new ObservableValue(-33),
                columnStyle: TableColumnStyle.Secondary,
                onSize: this._onSizeColumn
            },
            {
                id: "permissions",
                name: Resources.PermissionColumnName,
                renderCell: (
                    rowIndex: number,
                    columnIndex: number,
                    tableColumn: ITableColumn<IProjectPermissionMembers>,
                    tableItem: IProjectPermissionMembers
                ) => this._renderPermissionsColumn(rowIndex, columnIndex, tableColumn, tableItem, this.props.permissionLevelDisplayNames),
                width: new ObservableValue(-33),
                columnStyle: TableColumnStyle.Secondary,
                onSize: this._onSizeColumn
            }
        ];
    }

    private _onSizeColumn(event: MouseEvent, index: number, width: number, column: ITableColumn<IProjectPermissionMembers>) {
        (column.width as IObservableValue<number>).value = width;
    }

    private _renderNameColumn = (
        rowIndex: number,
        columnIndex: number,
        tableColumn: ITableColumn<IProjectPermissionMembers>,
        tableItem: any
    ): JSX.Element => {
        return (
            <TwoLineTableCell
                columnIndex={columnIndex}
                tableColumn={tableColumn}
                key={columnIndex}
                line1={
                    <Tooltip overflowOnly={true}>
                        <span className="fontWeightSemiBold bolt-table-two-line-cell-item text-ellipsis">{tableItem.member.displayName}</span>
                    </Tooltip>
                }
                line2={
                    <Tooltip overflowOnly={true}>
                        <span className="fontSizeMS bolt-table-two-line-cell-item secondary-text flex-row flex-baseline text-ellipsis">
                            {tableItem.subjectKind === "group" ? tableItem.member.description : tableItem.member.mail}
                        </span>
                    </Tooltip>
                }
                iconProps={{
                    render: (className?: string | undefined): JSX.Element => this._renderImageIcon(tableItem, className)
                }}
            />
        );
    };

    private _renderImageIcon(tableItem: IProjectPermissionMembers, className?: string | undefined): JSX.Element {
        return (
            <Persona
                className="project-permissions-members-persona"
                size={PersonaSize.size40}
                identity={tableItem.member}
                personaProvider={this._personaDetailsProvider}
            />
        );
    }

    private _renderTypeColumn(
        rowIndex: number,
        columnIndex: number,
        tableColumn: ITableColumn<IProjectPermissionMembers>,
        tableItem: IProjectPermissionMembers
    ): JSX.Element {
        return (
            <SimpleTableCell
                columnIndex={columnIndex}
                tableColumn={tableColumn}
                key={columnIndex}
                contentClassName="project-permissions-members-details"
            >
                <Tooltip overflowOnly={true}>
                    <span className="text-ellipsis">{tableItem.member.entityType}</span>
                </Tooltip>
            </SimpleTableCell>
        );
    }

    private _renderPermissionsColumn = (
        rowIndex: number,
        columnIndex: number,
        tableColumn: ITableColumn<IProjectPermissionMembers>,
        tableItem: IProjectPermissionMembers,
        menuItems: IObservableArray<IListBoxItem<string>>
    ): JSX.Element => {
        return (
            <SimpleTableCell
                columnIndex={columnIndex}
                tableColumn={tableColumn}
                key={columnIndex}
                contentClassName="project-permissions-members-details"
                children={
                    <Dropdown
                        placeholder={this._getProjectLevelPermissionName(tableItem.permissionLevelAssignment.permissionLevelDefinitionId)}
                        items={menuItems}
                        onSelect={(event: React.SyntheticEvent<HTMLElement>, item: IListBoxItem<string>) =>
                            this._onSelectedPermissionChanged(
                                event,
                                item,
                                tableItem.permissionLevelAssignment.permissionLevelDefinitionId,
                                tableItem.permissionLevelAssignment.subject,
                                tableItem
                            )
                        }
                        className="project-permissions-dropdown"
                    />
                }
            />
        );
    };

    private _onSelectedPermissionChanged = (
        event: React.SyntheticEvent<HTMLElement>,
        selectedItem: IListBoxItem<string>,
        previousItemId: string,
        subjectDescriptor: any,
        tableItem: IProjectPermissionMembers
    ): void => {
        var actionTelemetryLogger = this.props.startActionCapture("Look up permission level assignments", {});

        if (selectedItem && subjectDescriptor && selectedItem.id && previousItemId) {
            // No-op on invalid data
            this.props.updatePermissionLevel(subjectDescriptor, selectedItem.id, previousItemId).then(
                assign => {
                    tableItem.permissionLevelAssignment = assign;
                },
                error => {
                    // TODO - update UX error experience
                    actionTelemetryLogger.captureError(ParseErrorFromAPI(error));
                }
            );
        }
    };

    private _renderLoadRow = (index: number, item: IProjectPermissionMembers, details: ITableRowDetails<IProjectPermissionMembers>): JSX.Element => {
        this._onRenderMissingItem();

        return (
            <TableRow key={index} index={index} details={details}>
                <SimpleTableCell columnIndex={1}>
                    <div>
                        <Spinner label={Resources.Loading} size={SpinnerSize.large} />
                    </div>
                </SimpleTableCell>
            </TableRow>
        );
    };

    private _onRenderMissingItem = (): null => {
        this.props.startActionCapture("Load more users on scroll", {});

        this.props.fetchMoreItems();
        return null;
    };

    private _getProjectLevelPermissionName(definitionId: string): string {
        let def = this.props.permissionLevelDefinition.find(x => x.id === definitionId);

        return def ? def.name : "";
    }
}
